import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hexagon, Mail, CheckCircle, Key, Link as LinkIcon } from "lucide-react";
import { EmailVerificationCodeInput } from "@/components/EmailVerificationCodeInput";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FcGoogle } from "react-icons/fc";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { useSecurityLogger } from "@/hooks/useSecurityLogger";
import type { GeolocationData } from "@/hooks/useIPGeolocation";
import { loginSchema, signupSchema } from "@/schemas/authSchema";
import { z } from "zod";
import { SecurityPreferenceDialog } from "@/components/SecurityPreferenceDialog";
import { resolveDefaultLanding } from "@/lib/getDefaultLanding";

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationType, setVerificationType] = useState<'link' | 'code'>('link');
  const [verificationUserId, setVerificationUserId] = useState<string>("");
  const [showMagicLinkOption, setShowMagicLinkOption] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showSecurityDialog, setShowSecurityDialog] = useState(false);
  const [securityDialogUserId, setSecurityDialogUserId] = useState<string>("");
  const [dialogShownThisSession, setDialogShownThisSession] = useState(false);
  const [isSecurityDialogActive, setIsSecurityDialogActive] = useState(false);
  const navigate = useNavigate();
  const deviceFingerprint = useDeviceFingerprint();
  const { logSecurityEvent } = useSecurityLogger();
  const [geolocation, setGeolocation] = useState<GeolocationData | null>(null);
  const hasCheckedAuth = useRef(false);
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  // Read & validate ?redirect=<path> — only allow internal paths.
  // Rejects absolute URLs, protocol-relative (//host) and back-slash tricks (/\host).
  const redirectParam = useMemo(() => {
    const raw = searchParams.get("redirect");
    if (!raw) return null;
    if (!raw.startsWith("/")) return null;
    if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
    return raw;
  }, [searchParams]);

  // Fetch IP and geolocation on mount
  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const { data } = await supabase.functions.invoke('get-client-info', { body: {} });
        if (data) {
          setGeolocation({
            ip_address: data.ip_address || "Unknown",
            country: data.geolocation?.country,
            city: data.geolocation?.city,
            region: data.geolocation?.region,
            latitude: data.geolocation?.latitude,
            longitude: data.geolocation?.longitude,
            timezone: data.geolocation?.timezone,
          });
        }
      } catch (error) {
        console.error("Failed to fetch client info:", error);
      }
    };
    fetchClientInfo();
  }, []);

  // Auto-redirect if user is already logged in (only check once on mount)
  useEffect(() => {
    if (hasCheckedAuth.current) return;
    
    const checkUser = async () => {
      if (isLoading || isSecurityDialogActive) return;
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        hasCheckedAuth.current = true;
        const landing = redirectParam ?? (await resolveDefaultLanding(session.user.id));
        navigate(landing);
      }
    };
    checkUser();
  }, [navigate, isLoading, isSecurityDialogActive]);

  // Handle email link verification and show security preference dialog
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user && !dialogShownThisSession) {
          // Defer the database query to break out of the auth callback
          setTimeout(async () => {
            // Check if this is from email verification
            const { data: profile } = await supabase
              .from('profiles')
              .select('initial_verification_complete, verification_method')
              .eq('id', session.user.id)
              .single();
            
            // Show security dialog if not completed yet
            if (!profile?.initial_verification_complete) {
              setIsSecurityDialogActive(true);
              setDialogShownThisSession(true);
              setShowSecurityDialog(true);
              setSecurityDialogUserId(session.user.id);
            }
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [dialogShownThisSession]);

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;

    if (!email) {
      setErrors({ email: t("auth.emailRequired") });
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: t("auth.resetEmailSent"),
        description: t("auth.checkEmailForReset"),
      });
    } catch (error: any) {
      console.error("Forgot password error:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("auth.resetEmailFailed"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const data = {
      email: formData.get("signup-email") as string,
      password: formData.get("signup-password") as string,
      confirmPassword: formData.get("confirm-password") as string,
      fullName: formData.get("signup-name") as string,
      inviteCode: formData.get("invite-code") as string,
    };

    // Use unified signup check
    const { data: canSignup } = await supabase.rpc('check_can_signup') as { data: any };

    if (!canSignup?.allowed) {
      // Fetch custom disabled message for signup_disabled reason
      if (canSignup?.reason === 'signup_disabled') {
        const { data: settings } = await supabase
          .from('platform_settings')
          .select('setting_value')
          .eq('setting_key', 'signup_config')
          .single();

        const customMessage = (settings?.setting_value as any)?.signup_disabled_message;
        setErrors({ email: customMessage || canSignup.message });
      } else {
        setErrors({ email: canSignup?.message || "Sign-ups are currently unavailable." });
      }
      setIsLoading(false);
      return;
    }

    // If allowed but requires invitation code
    if (canSignup.requires_code && !data.inviteCode) {
      setErrors({ inviteCode: canSignup.message });
      setIsLoading(false);
      return;
    }

    // Validate invitation code if provided
    if (data.inviteCode) {
      const { data: validation } = await supabase.rpc('validate_invitation_code', {
        p_code: data.inviteCode
      }) as { data: { valid: boolean; error?: string } | null };

      if (!validation?.valid) {
        setErrors({ inviteCode: validation?.error || "Invalid invitation code" });
        setIsLoading(false);
        return;
      }
    }

    // Validate with Zod
    try {
      signupSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        setIsLoading(false);
        toast({
          title: t("auth.validationError"),
          description: t("auth.checkForm"),
          variant: "destructive",
        });
        return;
      }
    }

    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.fullName,
          preferred_language: i18n.language,
        },
        emailRedirectTo: `${window.location.origin}/dashboard?verified=true`,
      },
    });

    // Mark invitation code as used if provided
    if (!error && authData.user && data.inviteCode) {
      await supabase.rpc('mark_invitation_code_used', {
        p_code: data.inviteCode,
        p_used_by: authData.user.id,
      });
    }

    setIsLoading(false);

    if (error) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Show verification message instead of navigating
      setVerificationEmail(data.email);
      setVerificationUserId(authData.user?.id || '');
      setShowVerificationMessage(true);
      
      // Trigger verification email send with selected method
      try {
        await supabase.functions.invoke('send-verification-email', {
          body: {
            email: data.email,
            user_id: authData.user?.id,
            verification_type: verificationType
          }
        });
      } catch (emailError) {
        console.error("Failed to send verification email:", emailError);
        // Don't show error to user - they can still verify via default Supabase email
      }
      
      toast({
        title: t("common.success"),
        description: verificationType === 'code' 
          ? t("auth.checkEmailForCode")
          : t("auth.checkEmailVerification"),
      });
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const data = {
      email: formData.get("email") as string,
      password: formData.get("password") as string,
    };

    // Validate with Zod
    try {
      loginSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        setIsLoading(false);
        toast({
          title: t("auth.validationError"),
          description: t("auth.checkForm"),
          variant: "destructive",
        });
        return;
      }
    }

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      setIsLoading(false);
      // Log failed login attempt
      try {
        const { data: recentAttempts } = await supabase
          .from('failed_login_attempts')
          .select('*')
          .eq('email', data.email)
          .gte('attempt_time', new Date(Date.now() - 3600000).toISOString())
          .order('attempt_time', { ascending: false });

        const attemptCount = (recentAttempts?.length || 0) + 1;
        const isSuspicious = attemptCount >= 3;
        const riskScore = Math.min(attemptCount * 20, 100);

        await supabase
          .from('failed_login_attempts')
          .insert({
            email: data.email,
            ip_address: geolocation?.ip_address || 'Unknown',
            user_agent: navigator.userAgent,
            geolocation: geolocation ? {
              country: geolocation.country,
              city: geolocation.city,
              region: geolocation.region,
              timezone: geolocation.timezone,
            } : null,
            failure_reason: error.message,
            device_fingerprint: deviceFingerprint?.fingerprint_hash || null,
            is_suspicious: isSuspicious,
            risk_score: riskScore,
          });

        if (isSuspicious) {
          await logSecurityEvent({
            event_type: "multiple_failed_attempts",
            description: `Multiple failed login attempts detected for ${data.email} (${attemptCount} attempts in last hour)`,
            severity: "high",
            user_id: undefined,
            was_blocked: false,
            details: {
              email: data.email,
              attempt_count: attemptCount,
              error_message: error.message,
              risk_score: riskScore,
            },
          });
        } else {
          await logSecurityEvent({
            event_type: "failed_login",
            description: `Failed login attempt for ${data.email}`,
            severity: "low",
            user_id: undefined,
            was_blocked: false,
            details: {
              email: data.email,
              error_message: error.message,
            },
          });
        }
      } catch (logError) {
        console.error("Failed to log failed login attempt:", logError);
      }

      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    // Check user's verification method preference
    const { data: profile } = await supabase
      .from('profiles')
      .select('verification_method, email_verified_at, initial_verification_complete')
      .eq('id', authData.user.id)
      .single();

    // If dialog will be shown, don't navigate - let dialog handle it
    if (!profile?.initial_verification_complete) {
      setIsLoading(false);
      return;
    }

    // If user wants magic link login, redirect to magic link flow
    if (profile?.verification_method === 'magic_link') {
      setIsLoading(false);
      await handleMagicLinkLogin(data.email);
      return;
    }

    // If user wants always_verify, require email verification
    if (profile?.verification_method === 'always_verify') {
      setIsLoading(false);
      try {
        await supabase.functions.invoke('send-verification-email', {
          body: { 
            email: data.email, 
            user_id: authData.user.id, 
            verification_type: 'link' 
          }
        });
        
        setShowVerificationMessage(true);
        setVerificationEmail(data.email);
        setVerificationType('link');
        toast({
          title: "Verification Required",
          description: "Please check your email to complete login.",
        });
      } catch (error) {
        console.error("Failed to send verification email:", error);
        toast({
          title: t("common.error"),
          description: "Failed to send verification email",
          variant: "destructive",
        });
      }
      return;
    }

    // Standard login - check maintenance mode and redirect
    const { data: isMaintenanceMode } = await supabase.rpc('is_feature_enabled', {
      p_flag_key: 'maintenance_mode'
    });

    if (isMaintenanceMode) {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', authData.user?.id);

      const isAdmin = userRoles?.some(r => r.role === 'admin');

      if (isAdmin) {
        toast({
          title: t("common.success"),
          description: "Welcome, Admin. Platform is in maintenance mode.",
        });
        navigate("/admin");
        setIsLoading(false);
      } else {
        toast({
          title: "Maintenance Mode",
          description: "Platform is currently under maintenance.",
          variant: "destructive",
        });
        navigate("/maintenance");
        setIsLoading(false);
      }
    } else {
      toast({
        title: t("common.success"),
        description: t("auth.loginSuccess"),
      });

      // Defer navigation to ensure auth state has updated
      const landing = redirectParam ?? (await resolveDefaultLanding(authData.user!.id));
      setTimeout(() => {
        navigate(landing);
        setIsLoading(false);
      }, 100);
    }
  };

  const handleMagicLinkLogin = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${redirectParam ?? "/dashboard"}`,
        },
      });

      if (error) throw error;

      setMagicLinkSent(true);
      toast({
        title: "Magic Link Sent",
        description: "Check your email for a login link.",
      });
    } catch (error: any) {
      console.error("Magic link error:", error);
      toast({
        title: t("common.error"),
        description: error.message || "Failed to send magic link",
        variant: "destructive",
      });
    }
  };

  const handleOAuthLogin = async (provider: "google") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}${redirectParam ?? "/dashboard"}`,
      },
    });

    if (error) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-card/30 px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8 group">
          <div className="bg-primary p-2 rounded-lg group-hover:shadow-[var(--glow-primary)] transition-shadow duration-300">
            <Hexagon className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Silva Labs
          </span>
        </Link>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            {forgotPasswordMode ? (
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl">{t("auth.forgotPassword")}</CardTitle>
                  <CardDescription>{t("auth.enterEmailForReset")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {resetEmailSent ? (
                    <div className="space-y-4">
                      <div className="flex justify-center mb-4">
                        <div className="rounded-full bg-primary/10 p-6">
                          <Mail className="h-12 w-12 text-primary" />
                        </div>
                      </div>
                      <div className="text-center space-y-2">
                        <p className="font-medium">{t("auth.resetEmailSent")}</p>
                        <p className="text-sm text-muted-foreground">
                          {t("auth.checkEmailForReset")}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setForgotPasswordMode(false);
                          setResetEmailSent(false);
                        }}
                      >
                        {t("common.back")}
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email">{t("auth.email")}</Label>
                        <Input
                          id="forgot-email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          required
                          className="bg-background"
                        />
                        {errors.email && (
                          <p className="text-sm text-destructive">{errors.email}</p>
                        )}
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading}
                      >
                        {isLoading ? t("common.loading") : t("auth.sendResetLink")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full"
                        onClick={() => setForgotPasswordMode(false)}
                      >
                        {t("common.back")}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            ) : (
            <Card className="border-border bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl">Welcome Back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      required
                      className="bg-background"
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button 
                        type="button"
                        onClick={() => setForgotPasswordMode(true)}
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      required
                      minLength={8}
                      className="bg-background"
                    />
                    {errors.password && (
                      <p className="text-sm text-destructive">{errors.password}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Logging in..." : "Login"}
                  </Button>
                  
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const emailInput = document.getElementById('email') as HTMLInputElement;
                      if (emailInput?.value) {
                        handleMagicLinkLogin(emailInput.value);
                      } else {
                        toast({
                          title: t("common.error"),
                          description: "Please enter your email first",
                          variant: "destructive",
                        });
                      }
                    }}
                    disabled={isLoading || magicLinkSent}
                    className="w-full"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {magicLinkSent ? "Magic Link Sent - Check Email" : "Send Magic Link to Login"}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOAuthLogin("google")}
                  className="w-full"
                >
                  <FcGoogle className="w-5 h-5 mr-2" />
                  Continue with Google
                </Button>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Sign Up Tab */}
          <TabsContent value="signup">
            {showVerificationMessage ? (
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <div className="rounded-full bg-primary/10 p-6">
                      <Mail className="h-12 w-12 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-2xl text-center">
                    {t("auth.checkYourEmail")}
                  </CardTitle>
                  <CardDescription className="text-center">
                    {t("auth.verificationEmailSent", { email: verificationEmail })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {verificationType === 'code' ? (
                  <EmailVerificationCodeInput
                    email={verificationEmail}
                    userId={verificationUserId}
                    onSuccess={async () => {
                      // Check if user needs to set security preference
                      const { data: profile } = await supabase
                        .from('profiles')
                        .select('initial_verification_complete')
                        .eq('id', verificationUserId)
                        .single();
                      
                      if (!profile?.initial_verification_complete) {
                        setShowSecurityDialog(true);
                        setSecurityDialogUserId(verificationUserId);
                      } else {
                        navigate('/dashboard?verified=true');
                      }
                    }}
                    onResend={async () => {
                      await supabase.functions.invoke('send-verification-email', {
                        body: { email: verificationEmail, user_id: verificationUserId, verification_type: 'code' }
                      });
                    }}
                  />
                  ) : (
                    <>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{t("auth.verificationStep1")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("auth.verificationStep1Desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{t("auth.verificationStep2")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("auth.verificationStep2Desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{t("auth.verificationStep3")}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("auth.verificationStep3Desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setShowVerificationMessage(false);
                        setVerificationEmail("");
                      }}
                    >
                      {t("common.back")}
                    </Button>
                  </div>
                  </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl">Create Account</CardTitle>
                  <CardDescription>Sign up to start your learning journey</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <Input
                        id="signup-name"
                        name="signup-name"
                        type="text"
                        placeholder="John Doe"
                        required
                        className="bg-background"
                      />
                      {errors.fullName && (
                        <p className="text-sm text-destructive">{errors.fullName}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        name="signup-email"
                        type="email"
                        placeholder="john@example.com"
                        required
                        className="bg-background"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        name="signup-password"
                        type="password"
                        placeholder="••••••••"
                        required
                        minLength={8}
                        className="bg-background"
                      />
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Must be at least 8 characters with uppercase, lowercase, number, and special character
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <Input
                        id="confirm-password"
                        name="confirm-password"
                        type="password"
                        placeholder="••••••••"
                        required
                        minLength={8}
                        className="bg-background"
                      />
                      {errors.confirmPassword && (
                        <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label>Verification Method (Optional)</Label>
                      <RadioGroup value={verificationType} onValueChange={(value) => setVerificationType(value as 'link' | 'code')}>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="link" id="link" />
                          <Label htmlFor="link" className="font-normal cursor-pointer flex items-center gap-2">
                            <LinkIcon className="w-4 h-4" />
                            Email Link (Default)
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="code" id="code" />
                          <Label htmlFor="code" className="font-normal cursor-pointer flex items-center gap-2">
                            <Key className="w-4 h-4" />
                            6-Digit Code (More Secure)
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-code">Invitation Code (if you have one)</Label>
                      <Input
                        id="invite-code"
                        name="invite-code"
                        type="text"
                        placeholder="SILVA-XXXXX"
                        className="bg-background uppercase"
                      />
                      {errors.inviteCode && (
                        <p className="text-sm text-destructive">{errors.inviteCode}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Optional unless invitation-only mode is enabled
                      </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? "Creating account..." : "Sign Up"}
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOAuthLogin("google")}
                    className="w-full"
                  >
                    <FcGoogle className="w-5 h-5 mr-2" />
                    Continue with Google
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <SecurityPreferenceDialog
          open={showSecurityDialog}
          userId={securityDialogUserId}
          onComplete={() => {
            setShowSecurityDialog(false);
            setIsSecurityDialogActive(false);
            setDialogShownThisSession(true);
            navigate('/dashboard?verified=true');
          }}
        />

        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our{" "}
          <a href="#" className="text-primary hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="#" className="text-primary hover:underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
